/* klaviyo-atc.js - Klaviyo "Added to Cart", for every way something gets into the cart.

   Klaviyo's Shopify integration sends Viewed Product, Started Checkout and Placed
   Order, but nothing for Added to Cart - and on this store that is where most people
   stop. Without the event there is no abandoned-cart flow at all.

   Things reach the cart six different ways here: the editor iframes hand a line to
   the parent page, which posts /cart/add.js; product pages submit a form; the saved
   cart restore posts a batch; and so on. Wiring the event into each would miss the
   next one somebody adds. So this does not hook the adders at all. It compares the
   cart with what it held last time and reports whatever is new:

     - on every page load (catches form posts, which navigate away)
     - after any fetch/XHR to /cart/add, /cart/change, /cart/update or /cart/clear

   The last-seen cart is kept in localStorage, keyed by line. With no snapshot yet
   (first visit, cleared storage) it only records a baseline and reports nothing,
   so an old cart can never be reported as a fresh add.

   CheckoutURL is a saved-cart link from <editor>/c/save, not /cart. Most of these
   shoppers start inside Instagram's or Facebook's browser, where /cart is empty the
   moment they open the email anywhere else. The saved link rebuilds the cart on any
   device (cart-save.js does the restore). If saving fails it falls back to /cart.

   Klaviyo only records events for visitors it can identify - someone who clicked a
   Klaviyo email, filled a Klaviyo form, or used Email me my cart - which is also
   exactly who an abandoned-cart email can reach.

   The editor origin comes from data-editor on the script tag. */
(function () {
  var script = document.currentScript;
  var EDITOR = ((script && script.getAttribute('data-editor')) || 'https://editor.wallspoke.com').replace(/\/+$/, '');
  var KEY = 'ws_klaviyo_cart_v1';
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function readSnap() { try { return JSON.parse(localStorage.getItem(KEY)); } catch (e) { return null; } }
  function writeSnap(s) { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) {} }
  function money(c) { return Math.round(c) / 100; }

  // A line is its design, not its variant (two maps can share a variant).
  function lineKey(it) {
    var p = it.properties || {};
    return it.variant_id + '|' + (p._preview_url || p._spec || JSON.stringify(p));
  }
  function imageOf(it) {
    var p = it.properties || {};
    return p._preview_url || it.image || null;
  }

  /* Store the cart on the editor service and get back a link that rebuilds it. No
     email is sent: /c/save only emails when it is given an address. */
  async function savedCartUrl(cart) {
    try {
      var r = await fetch(EDITOR + '/c/save', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          total: cart.total_price, original_total: cart.original_total_price,
          discount_codes: (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; }),
          items: cart.items.map(function (i) {
            return { variant_id: i.variant_id, quantity: i.quantity, properties: i.properties || {},
                     title: i.product_title, variant: i.product_has_only_default_variant ? null : i.variant_title,
                     image: i.image, line_price: i.original_line_price };
          })
        })
      });
      if (r.ok) { var j = await r.json(); if (j && j.url) return j.url; }
    } catch (e) {}
    return location.origin + '/cart';
  }

  // Klaviyo drops events from visitors it cannot identify, so only they are worth a
  // stored cart. Unknown (older script, still loading) counts as identified.
  async function identified() {
    try {
      if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') return !!(await window.klaviyo.isIdentified());
    } catch (e) {}
    return true;
  }

  async function track(cart, added) {
    var url = (await identified()) ? await savedCartUrl(cart) : location.origin + '/cart';
    var items = cart.items.map(function (i) {
      return {
        ProductID: i.product_id, VariantID: i.variant_id, SKU: i.sku || null,
        ProductName: i.product_title, VariantName: i.product_has_only_default_variant ? null : i.variant_title,
        Quantity: i.quantity, ItemPrice: money(i.final_price), RowTotal: money(i.final_line_price),
        ProductURL: location.origin + i.url, ImageURL: imageOf(i)
      };
    });
    var klaviyo = window.klaviyo = window.klaviyo || [];
    added.forEach(function (a) {
      var it = a.item;
      klaviyo.push(['track', 'Added to Cart', {
        $value: money(cart.total_price),
        AddedItemProductName: it.product_title,
        AddedItemProductID: it.product_id,
        AddedItemVariantID: it.variant_id,
        AddedItemSKU: it.sku || null,
        AddedItemVariantName: it.product_has_only_default_variant ? null : it.variant_title,
        AddedItemImageURL: imageOf(it),
        AddedItemURL: location.origin + it.url,
        AddedItemPrice: money(it.final_price),
        AddedItemQuantity: a.qty,
        ItemNames: cart.items.map(function (i) { return i.product_title; }),
        ItemCount: cart.item_count,
        DiscountCode: ((cart.discount_codes || []).filter(function (d) { return d.applicable; })[0] || {}).code || null,
        CheckoutURL: url,
        Items: items
      }]);
    });
  }

  var busy = false, again = false;
  async function check() {
    if (busy) { again = true; return; }
    busy = true;
    try {
      var cart = await nativeFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      var now = {};
      (cart.items || []).forEach(function (it) { now[lineKey(it)] = { q: it.quantity, item: it }; });
      var prev = readSnap();
      var snap = {};
      Object.keys(now).forEach(function (k) { snap[k] = now[k].q; });
      writeSnap(snap);
      if (prev) {
        var added = [];
        Object.keys(now).forEach(function (k) {
          var was = prev[k] || 0;
          if (now[k].q > was) added.push({ item: now[k].item, qty: now[k].q - was });
        });
        if (added.length) await track(cart, added);
      }
    } catch (e) {
    } finally {
      busy = false;
      if (again) { again = false; check(); }
    }
  }

  // Watch the cart writes this page makes, whichever code makes them.
  var nativeFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    var p = nativeFetch(input, init);
    try {
      var u = typeof input === 'string' ? input : (input && input.url) || '';
      if (CART_WRITE.test(u)) p.then(function (r) { if (r.ok) setTimeout(check, 0); }, function () {});
    } catch (e) {}
    return p;
  };
  var xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      if (CART_WRITE.test(String(url))) this.addEventListener('load', function () { if (this.status < 400) check(); });
    } catch (e) {}
    return xhrOpen.apply(this, arguments);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check);
  else check();
})();
